/**
 * THEME TOGGLE v2.0 - ULTRA OPTIMIZED
 * ✅ React.memo para componentes
 * ✅ useCallback para toggle
 * ✅ useMemo para clases dinámicas
 */
import { useMemo, useCallback, memo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/solid';

const ThemeToggle = memo(function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();

  const isLight = useMemo(() => theme === 'light', [theme]);

  const ariaLabel = useMemo(() => 
    `Cambiar a modo ${isLight ? 'oscuro' : 'claro'}`
  , [isLight]);

  const switchClasses = useMemo(() => 
    `absolute top-0.5 left-0.5 w-6 h-6 rounded-full shadow-lg transition-transform duration-300 flex items-center justify-center ${
      isLight 
        ? 'translate-x-7 bg-yellow-400' 
        : 'translate-x-0 bg-gray-900'
    }`
  , [isLight]);

  const trackClasses = useMemo(() => 
    `absolute inset-0 rounded-full transition-colors duration-300 ${
      isLight
        ? 'bg-gradient-to-r from-blue-400 to-purple-400'
        : 'bg-gradient-to-r from-gray-700 to-gray-800'
    }`
  , [isLight]);

  return (
    <button
      onClick={toggleTheme}
      className="relative w-14 h-7 rounded-full bg-gray-700 dark:bg-gray-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 active:scale-95"
      aria-label={ariaLabel}
    >
      <div className={trackClasses} />
      
      <div className={switchClasses}>
        {isLight ? (
          <ThemeSunIcon />
        ) : (
          <ThemeMoonIcon />
        )}
      </div>
    </button>
  );
});

// ✅ COMPONENTES MEMOIZADOS

const ThemeSunIcon = memo(function ThemeSunIcon() {
  return (
    <SunIcon className="w-4 h-4 text-yellow-900 animate-spin-slow" />
  );
});

const ThemeMoonIcon = memo(function ThemeMoonIcon() {
  return (
    <MoonIcon className="w-4 h-4 text-blue-300" />
  );
});

export default ThemeToggle;
